import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ChevronLeft, ChevronRight, ChevronDown, ShoppingCart,
  Leaf, ShieldCheck, Sparkles, Sprout, Heart, Flame, Brain, Star, Dumbbell, Gift,
} from "lucide-react";
import { Stars } from "@/components/StarRating";
import { useCart } from "@/context/CartContext";
import { imgSrc } from "@/lib/img";
import { CATEGORY_DATA } from "@/data/categoryData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { leaf: Leaf, shield: ShieldCheck, sparkles: Sparkles, sprout: Sprout, heart: Heart, flame: Flame, brain: Brain, star: Star, dumbbell: Dumbbell, gift: Gift };
const CATEGORY_ORDER = ["moringa-greens", "adaptogens-herbs", "seeds-nuts", "superfood-blends", "proteins-plant-powders", "wellness-essentials", "kids-nutrition", "pickles-chutneys", "gifting"];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const addDemo = (name) => toast.success(`${name} added to cart (demo)`);
const realPrice = (p) => p.price_from ?? p.final_price ?? p.price;

// Mock placeholder card (shown until real products are assigned to this category)
const MockProductCard = ({ p, img }) => (
  <div data-testid={`product-card-${slugify(p.name + "-" + p.sub)}`} className="bg-white rounded-2xl p-3.5 border border-forest/5 shadow-sm flex flex-col">
    <div className="rounded-xl overflow-hidden bg-[#FBF7EC] aspect-square">
      <img src={img} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
    </div>
    <h3 className="font-semibold text-forest text-[13px] uppercase tracking-wide mt-3 leading-tight">{p.name}</h3>
    <p className="text-[11px] text-mutedgreen mt-0.5">({p.sub})</p>
    <div className="flex items-center gap-1.5 mt-1.5">
      <Stars value={p.rating} size={12} />
      <span className="text-[11px] text-mutedgreen">{p.rating} ({p.count})</span>
    </div>
    <p className="text-forest font-bold mt-1.5 mb-2.5">₹ {p.price}</p>
    <button onClick={() => addDemo(p.name)} data-testid="add-to-cart" className="mt-auto w-full bg-forest hover:bg-gold text-cream text-[11px] font-semibold uppercase tracking-wide py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors">
      <ShoppingCart size={13} /> Add to Cart
    </button>
    <Link to="/collection" className="text-[11px] text-mutedgreen hover:text-forest mt-2 inline-flex items-center gap-1">View Product <ArrowRight size={11} /></Link>
  </div>
);

// Real product card — wired to the live cart with an inline size picker
const RealProductCard = ({ p }) => {
  const { add } = useCart();
  const hasVariants = p.variants?.length > 0;
  const productAvailable = (p.stock || "in_stock") === "in_stock";
  const firstInStock = hasVariants ? (p.variants.find((v) => v.stock === "in_stock") || p.variants[0]) : null;
  const [vid, setVid] = useState(firstInStock?.id);
  const selected = hasVariants ? p.variants.find((v) => v.id === vid) : null;
  const price = selected ? selected.final_price : (p.final_price ?? p.price);
  const mrp = selected ? selected.price : p.price;
  const showStrike = !!p.discount_label && price < mrp;
  const canAdd = productAvailable && (!hasVariants || (selected && selected.stock === "in_stock"));
  return (
    <div data-testid={`product-card-${p.id}`} className="bg-white rounded-2xl p-3.5 border border-forest/5 shadow-sm flex flex-col">
      <Link to={`/product/${p.id}`} className="rounded-xl overflow-hidden bg-[#FBF7EC] aspect-square block relative">
        <img src={imgSrc(p.image)} alt={p.name} className={`w-full h-full object-cover ${productAvailable ? "" : "grayscale-[40%]"}`} loading="lazy" />
        {p.badge && <span className="absolute top-2 left-2 bg-forest text-cream text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">{p.badge}</span>}
        {p.discount_label && <span data-testid={`product-discount-${p.id}`} className="absolute top-2 right-2 bg-gold text-white text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">{p.discount_label}</span>}
        {!productAvailable && <span className="absolute bottom-2 left-2 bg-cream/95 text-forest text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">{p.stock === "coming_soon" ? "Coming soon" : "Sold out"}</span>}
      </Link>
      <h3 className="font-semibold text-forest text-[13px] uppercase tracking-wide mt-3 leading-tight"><Link to={`/product/${p.id}`} className="hover:text-gold transition-colors">{p.name}</Link></h3>
      {p.tagline && <p className="text-[11px] text-mutedgreen mt-0.5 line-clamp-1">{p.tagline}</p>}
      {p.rating_count > 0 && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <Stars value={p.rating_avg} size={12} />
          <span className="text-[11px] text-mutedgreen">{p.rating_avg} ({p.rating_count})</span>
        </div>
      )}
      {hasVariants && (
        <div className="relative mt-2">
          <select
            data-testid={`size-select-${p.id}`}
            value={vid}
            onChange={(e) => setVid(e.target.value)}
            className="appearance-none w-full bg-[#FBF7EC] border border-forest/15 rounded-lg pl-3 pr-8 py-2 text-[11px] font-semibold text-forest focus:outline-none focus:border-forest/40"
          >
            {p.variants.map((v) => (
              <option key={v.id} value={v.id} disabled={v.stock !== "in_stock"}>
                {v.label} — ₹{v.final_price}{v.stock !== "in_stock" ? " (sold out)" : ""}
              </option>
            ))}
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-forest/50 pointer-events-none" />
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap mt-1.5 mb-2.5">
        <span className="text-forest font-bold">₹ {price}</span>
        {showStrike && <s data-testid={`product-mrp-${p.id}`} className="text-[11px] text-mutedgreen">₹ {mrp}</s>}
      </div>
      <button
        onClick={() => add(p.id, 1, true, hasVariants ? vid : null)}
        disabled={!canAdd}
        data-testid={`add-to-cart-${p.id}`}
        className="mt-auto w-full bg-forest hover:bg-gold text-cream text-[11px] font-semibold uppercase tracking-wide py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:hover:bg-forest disabled:cursor-not-allowed"
      >
        {canAdd ? <><ShoppingCart size={13} /> Add to Cart</> : "Unavailable"}
      </button>
      <Link to={`/product/${p.id}`} className="text-[11px] text-mutedgreen hover:text-forest mt-2 inline-flex items-center gap-1">View Product <ArrowRight size={11} /></Link>
    </div>
  );
};

export default function CategoryLanding() {
  const { slug } = useParams();
  const data = CATEGORY_DATA[slug];
  const [tab, setTab] = useState("all");
  const [sort, setSort] = useState("featured");
  const [meta, setMeta] = useState(null);
  const [real, setReal] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    setTab("all");
    setMeta(null);
    setReal([]);
    window.scrollTo({ top: 0 });
    axios.get(`${API}/categories`).then(({ data: cats }) => {
      setMeta(cats.find((c) => c.slug === slug) || null);
      // Preload adjacent category hero banners so arrow navigation has no image load jank
      const idx = CATEGORY_ORDER.indexOf(slug);
      [CATEGORY_ORDER[idx - 1], CATEGORY_ORDER[idx + 1]].forEach((s) => {
        if (!s) return;
        const c = cats.find((x) => x.slug === s);
        const src = c?.hero_banner ? imgSrc(c.hero_banner) : CATEGORY_DATA[s]?.heroImg;
        if (src) { const im = new Image(); im.src = src; }
      });
    }).catch(() => setMeta(null));
    axios.get(`${API}/products`, { params: { category: slug } }).then(({ data: ps }) => setReal(ps)).catch(() => setReal([]));
  }, [slug]);

  const display = useMemo(() => {
    if (!data) return [];
    let entries;
    if (tab === "all") {
      const reals = real.map((p) => ({ type: "real", key: `r-${p.id}`, p }));
      const needed = Math.max(0, 10 - reals.length);
      const mocks = data.products.slice(0, needed).map((p, i) => ({ type: "mock", key: `m-${i}`, p }));
      entries = [...reals, ...mocks];
    } else {
      entries = data.products.filter((p) => p.tags.includes(tab)).slice(0, 10).map((p, i) => ({ type: "mock", key: `m-${i}`, p }));
    }
    const price = (e) => (e.type === "real" ? realPrice(e.p) : e.p.price);
    const rating = (e) => (e.type === "real" ? (e.p.rating_avg || 0) : e.p.rating);
    if (sort === "price-low") entries = [...entries].sort((a, b) => price(a) - price(b));
    else if (sort === "price-high") entries = [...entries].sort((a, b) => price(b) - price(a));
    else if (sort === "rating") entries = [...entries].sort((a, b) => rating(b) - rating(a));
    return entries.slice(0, 10);
  }, [data, real, tab, sort]);

  if (!data) return <Navigate to="/shop-by-category" replace />;

  const { tint, tintDeep } = data;
  const name = meta?.name || data.name;
  const subhead = meta?.hero_caption || data.subhead;
  const desc = data.desc;
  const heroImg = meta?.hero_banner ? imgSrc(meta.hero_banner) : data.heroImg;
  const featureHeading = meta?.feature_heading || data.sidebar;
  const featureText = meta?.feature_text || data.featurePanel.lines;
  const featureImg = meta?.feature_image ? imgSrc(meta.feature_image) : data.featureImg;
  const whyBg = meta?.why_choose_background ? imgSrc(meta.why_choose_background) : null;
  const scrollBy = (d) => scroller.current?.scrollBy({ left: d * 300, behavior: "smooth" });

  const curIdx = CATEGORY_ORDER.indexOf(slug);
  const prevSlug = curIdx > 0 ? CATEGORY_ORDER[curIdx - 1] : null;
  const nextSlug = curIdx >= 0 && curIdx < CATEGORY_ORDER.length - 1 ? CATEGORY_ORDER[curIdx + 1] : null;
  const heroArrows = (
    <>
      {prevSlug && (
        <Link to={`/category/${prevSlug}`} data-testid="hero-prev-category" aria-label="Previous category" className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-forest/90 text-cream flex items-center justify-center hover:bg-gold transition-colors shadow-md">
          <ChevronLeft size={20} />
        </Link>
      )}
      {nextSlug && (
        <Link to={`/category/${nextSlug}`} data-testid="hero-next-category" aria-label="Next category" className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-forest/90 text-cream flex items-center justify-center hover:bg-gold transition-colors shadow-md">
          <ChevronRight size={20} />
        </Link>
      )}
    </>
  );

  return (
    <div key={slug} data-testid={`category-landing-${slug}`} className="bg-cream animate-fade-slide-in">
      {/* HERO */}
      {meta?.hero_banner ? (
        /* Custom uploaded banner — complete image shown end to end, no cropping; text overlaid on both, scaled down on mobile */
        <section className="relative overflow-hidden" data-testid={`category-hero-custom-${slug}`}>
          <img src={heroImg} alt={name} className="block w-full h-auto" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-nb max-w-xl">
              <p className="flex items-center gap-2 sm:gap-3 text-[8px] sm:text-[11px] font-bold uppercase tracking-[0.18em] sm:tracking-[0.2em] text-forest/80 mb-1 sm:mb-3">
                The Nutribest Collection · {data.num} <span className="h-px w-6 sm:w-12 bg-forest/30" />
              </p>
              <h1 className="font-serif text-xl sm:text-5xl lg:text-6xl text-forest leading-tight">{name}</h1>
              <p className="font-serif text-xs sm:text-2xl text-forest/90 mt-0.5 sm:mt-2">{subhead}</p>
              <p className="hidden sm:block text-mutedgreen sm:text-base sm:mt-4 max-w-md sm:leading-relaxed">{desc}</p>
              <Link to="/shop-by-category" data-testid="hero-all-categories-button" className="inline-flex items-center gap-1 sm:gap-2 bg-forest hover:bg-gold text-cream text-[8px] sm:text-xs font-semibold uppercase tracking-[0.14em] sm:tracking-[0.18em] px-3 py-1.5 sm:px-7 sm:py-3.5 rounded-full mt-1.5 sm:mt-7 transition-colors">
                All Categories <ArrowRight size={12} />
              </Link>
            </div>
          </div>
          {heroArrows}
        </section>
      ) : (
        /* Default coded illustration — keeps its colour tint and overlay */
        <section className="relative overflow-hidden" data-testid={`category-hero-default-${slug}`} style={{ background: `linear-gradient(120deg,${tint} 0%,${tintDeep} 100%)` }}>
          <img src={heroImg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-right opacity-95" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right,${tint} 22%,${tint}cc 40%,transparent 66%)` }} />
          <div className="relative container-nb py-14 sm:py-20 max-w-xl">
            <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-forest/80 mb-3">
              The Nutribest Collection · {data.num} <span className="h-px w-12 bg-forest/30" />
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest leading-tight">{name}</h1>
            <p className="font-serif text-xl sm:text-2xl text-forest/90 mt-2">{subhead}</p>
            <p className="text-mutedgreen mt-4 max-w-md leading-relaxed">{desc}</p>
            <Link to="/shop-by-category" data-testid="hero-all-categories-button" className="inline-flex items-center gap-2 bg-forest hover:bg-gold text-cream text-xs font-semibold uppercase tracking-[0.18em] px-7 py-3.5 rounded-full mt-7 transition-colors">
              All Categories <ArrowRight size={14} />
            </Link>
          </div>
          {heroArrows}
        </section>
      )}

      {/* FILTER BAR */}
      <section id="products" className="container-nb pt-10">
        <div className="flex flex-wrap items-center gap-2.5">
          {data.tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${tab === t.key ? "bg-forest text-cream border-forest" : "bg-white text-forest/80 border-forest/15 hover:border-forest/40"}`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-xs text-mutedgreen">
            <span className="hidden sm:inline">Sort by:</span>
            <div className="relative">
              <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="appearance-none bg-white border border-forest/15 rounded-full pl-4 pr-9 py-2 text-forest font-semibold focus:outline-none focus:border-forest/40">
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Top Rated</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/50 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* GRID with feature panel */}
      <section className="container-nb py-8">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="col-span-2 lg:col-span-1 lg:row-span-2 relative rounded-2xl overflow-hidden min-h-[260px] flex flex-col justify-between p-6" style={{ backgroundColor: tintDeep }}>
            <img src={featureImg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom,${tintDeep} 20%,${tintDeep}aa 42%,transparent 72%)` }} />
            <div className="relative">
              <h2 className="font-serif text-2xl text-forest leading-tight">{featureHeading}</h2>
              <p className="font-serif text-lg text-forest/80 mt-2 whitespace-pre-line">{featureText}</p>
            </div>
            <Link to="/collection" aria-label="Explore" className="relative w-11 h-11 rounded-full border border-forest/40 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors mt-6">
              <ArrowRight size={16} />
            </Link>
          </div>
          {display.map((e) =>
            e.type === "real" ? (
              <RealProductCard key={e.key} p={e.p} />
            ) : (
              <MockProductCard key={e.key} p={e.p} img={data.productImg} />
            )
          )}
          {display.length === 0 && <p className="col-span-full lg:col-span-5 text-center text-mutedgreen py-10">No products in this filter.</p>}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative overflow-hidden" style={whyBg ? undefined : { background: `linear-gradient(120deg,${tint} 0%,${tintDeep} 100%)` }}>
        {whyBg && <img src={whyBg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="relative container-nb py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div className="lg:pl-10">
            <p className="eyebrow !text-forest/70 mb-3">{data.why.eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest leading-tight">{data.why.title[0]}<br />{data.why.title[1]}</h2>
            <p className="text-mutedgreen mt-4 max-w-sm">{data.why.sub}</p>
            <Link to="/collection" className="inline-flex items-center gap-2 bg-forest hover:bg-gold text-cream text-xs font-semibold uppercase tracking-[0.18em] px-7 py-3.5 rounded-full mt-6 transition-colors">Explore More <ArrowRight size={14} /></Link>
          </div>
          <div className={`grid grid-cols-2 ${data.why.items.length >= 5 ? "sm:grid-cols-5" : "sm:grid-cols-4"} gap-y-6 lg:pr-10`}>
            {data.why.items.map(({ icon, title, text }, i) => {
              const Icon = ICONS[icon] || Leaf;
              return (
                <div key={title} className={`text-center px-4 ${i > 0 ? "sm:border-l sm:border-forest/20" : ""}`}>
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-forest/25 text-forest mb-3"><Icon size={22} /></span>
                  <h3 className="font-semibold text-forest text-sm leading-tight">{title}</h3>
                  <p className="text-[11px] text-mutedgreen mt-1">{text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="container-nb py-14">
        <div className="flex items-end justify-between gap-4 mb-7">
          <div>
            <p className="eyebrow !text-gold mb-1">Our Popular Picks</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest">Bestsellers</h2>
            <p className="text-mutedgreen text-sm mt-1">Loved by many. Trusted for good.</p>
          </div>
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scrollBy(-1)} data-testid="bs-prev" aria-label="Scroll left" className="w-10 h-10 rounded-full border border-forest/20 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => scrollBy(1)} data-testid="bs-next" aria-label="Scroll right" className="w-10 h-10 rounded-full border border-forest/20 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={scroller} className="flex gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {data.bestsellers.map((p, i) => (
            <div key={i} className="shrink-0 w-48 sm:w-52 bg-white rounded-2xl border border-forest/5 shadow-sm p-3 relative">
              <span className="absolute top-4 left-4 z-10 text-[9px] font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: p.badgeColor }}>{p.badge}</span>
              <div className="rounded-xl overflow-hidden bg-[#FBF7EC] aspect-square"><img src={data.productImg} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
              <h3 className="font-semibold text-forest text-sm mt-3 leading-tight">{p.name}</h3>
              <div className="flex items-center gap-1.5 mt-1"><Stars value={p.rating} size={11} /><span className="text-[10px] text-mutedgreen">{p.rating} ({p.count})</span></div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-forest font-bold">₹ {p.price}</p>
                <button onClick={() => addDemo(p.name)} aria-label="Add to cart" className="w-9 h-9 rounded-full bg-forest hover:bg-gold text-cream flex items-center justify-center transition-colors"><ShoppingCart size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MORE WAYS TO ENJOY */}
      <section className="relative overflow-hidden">
        <img src="/images/features-landscape.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-bottom" />
        <div className="absolute inset-0 bg-cream/55" />
        <div className="relative container-nb py-16">
          <h2 className="font-serif text-2xl sm:text-3xl text-forest text-center mb-10">More Ways to Enjoy</h2>
          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {data.more.map((m) => (
              <div key={m.title} className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 border-2 border-white shadow"><img src={data.productImg} alt="" className="w-full h-full object-cover" loading="lazy" /></div>
                <div>
                  <h3 className="font-semibold text-forest text-sm">{m.title}</h3>
                  <p className="text-xs text-mutedgreen mt-1 leading-relaxed">{m.text}</p>
                  <Link to={m.to || "/collection"} className="text-[11px] font-semibold text-forest uppercase tracking-wide inline-flex items-center gap-1 mt-2 hover:text-gold">{m.cta} <ArrowRight size={11} /></Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
